import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, Input, NgZone, OnDestroy, Renderer2 } from '@angular/core';
import { Subscription } from 'rxjs';
import { ColumnDef } from '../grid-ins.component';
import { ColumnSizes } from './grid-resizable.directive';
import { GridResizeEvent } from './grid-resize.model';
import { GridResizeService } from './grid-resize.service';

@Component({
    selector: 'skl-header-resizer',
    template: '',
    styleUrls: ['./header-resizer.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})

export class HeaderResizerComponent<TEntity, TCustomField = never> implements AfterViewInit, OnDestroy {

    @Input() public resizable = true;

    @Input() public columnDef!: ColumnDef<TEntity, TCustomField>;

    public headerElement!: HTMLElement;
    private minWidth!: number;
    private width!: number;
    private startWidth!: number;
    private startX!: number;
    private cellWidth!: number;
    private resizableMousemove!: () => void;
    private resizableMouseup!: () => void;
    private resizeColumn!: () => void;
    private pressed = false;
    private fillingColumnTouched = false;
    private fillingColumnTouchedSubscription!: Subscription;
    private viewport!: Element;

    public constructor(
        private zone: NgZone,
        private renderer: Renderer2,
        private elementRef: ElementRef<HTMLElement>,
        private gridResize: GridResizeService,
    ) { }

    public ngAfterViewInit(): void {
        const element = this.elementRef.nativeElement;
        const headerEl = element.closest('th');
        if (!headerEl) {
            throw new Error('HeaderResizerComponent must be used inside a <th> element');
        }
        this.headerElement = headerEl;

        const viewportEl = element.closest('table');
        if (!viewportEl) {
            throw new Error('HeaderResizerComponent must be used inside a <table> element');
        }
        this.viewport = viewportEl;

        this.renderer.removeStyle(document.body, 'pointer-events');
        this.renderer.removeStyle(document.documentElement, 'cursor');

        if (this.resizable) {
            this.renderer.addClass(this.headerElement, 'resizable');
            this.resizeColumn = this.renderer.listen(this.elementRef.nativeElement, 'mousedown', this.handleResizeColumn.bind(this));
        }

        this.fillingColumnTouchedSubscription = this.gridResize.on('FillingColumnTouched', this.viewport, () => {
            this.fillingColumnTouched = true;
        });
    }

    public ngOnDestroy(): void {
        this.resizeColumn?.();
        this.fillingColumnTouchedSubscription?.unsubscribe();
        this.resizableMousemove?.();
        this.resizableMouseup?.();
    }

    public initHeaderSize(columns: ColumnSizes<TEntity>[]): void {
        this.fillingColumnTouched = false;
        const column = columns.find(column => column.columnDef === this.columnDef);

        if (column) {
            this.initFillingColumn(column.fillingColumn);
            this.minWidth = column.minWidth ? parseInt(column.minWidth, 10) : NaN;
            this.width = column.width ? parseInt(column.width, 10) : NaN;
            this.setWidth(this.width, this.minWidth);
        }
    }

    private initFillingColumn(fillingColumn: boolean | undefined): void {
        if (fillingColumn && !this.fillingColumnTouched) {
            this.renderer.addClass(this.headerElement, 'filling-column');
        } else {
            this.renderer.removeClass(this.headerElement, 'filling-column');
        }
    }

    private setWidth(width: number, minWidth: number): void {
        if (minWidth && !isNaN(minWidth)) {
            this.renderer.setStyle(this.headerElement, 'min-width', `${minWidth}px`);
        } else {
            this.renderer.removeStyle(this.headerElement, 'min-width');
        }

        if (isNaN(width)) {
            this.renderer.removeStyle(this.headerElement, '--header-width', 2);
            return;
        }

        if (!minWidth || isNaN(minWidth)) {
            this.renderer.setStyle(this.headerElement, '--header-width', `${ width < 1 ? 1 : width }px`, 2);
        } else {
            this.headerElement && this.renderer.setStyle(this.headerElement, '--header-width', `max(${ minWidth }px, ${ width < 1 ? 1 : width }px)`, 2);
        }
    }

    private mouseMove(): void {
        this.resizableMousemove = this.renderer.listen('document', 'mousemove',
            (event: PointerEvent) => {
                requestAnimationFrame(() => {
                    if (this.pressed && event.buttons) {
                        const dx = event.pageX - this.startX;
                        const width = this.cellWidth = this.startWidth + dx;
                        this.renderer.addClass(this.headerElement, 'is-resizing');
                        this.setWidth(width, this.minWidth);
                        this.gridResize.emit(new GridResizeEvent('HeaderResizing', { width, columnDef: this.columnDef }, this.viewport));
                    }
                });
            }
        );
        this.resizableMouseup = this.renderer.listen('document', 'mouseup',
            (_event: PointerEvent) => {
                if (!this.pressed) return;

                this.pressed = false;
                this.resizableMousemove();
                this.resizableMouseup();
                this.renderer.removeStyle(document.body, 'pointer-events');
                this.renderer.removeStyle(document.documentElement, 'cursor');
                this.renderer.removeClass(this.headerElement, 'is-resizing');
                this.gridResize.emit(new GridResizeEvent('HeaderResizied', { width: this.cellWidth, columnDef: this.columnDef }, this.viewport));
            }
        );
    }

    protected handleResizeColumn(event: PointerEvent): void {
        this.zone.runOutsideAngular(() => {
            event.preventDefault();
            this.pressed = true;
            this.startX = event.pageX;
            this.startWidth = this.headerElement.clientWidth;
            this.setWidth(this.startWidth, this.minWidth);
            this.renderer.removeClass(this.headerElement, 'filling-column');
            this.renderer.setStyle(document.body, 'pointer-events', 'none', 1);
            this.renderer.setStyle(document.documentElement, 'cursor', 'col-resize', 1);
            this.gridResize.emit(new GridResizeEvent('HeaderResizerTouched', true, this.viewport));
            this.mouseMove();
        });
    }
}
